import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseFormComponent } from 'src/core/forms/base-form.component';
import { FreeSwitchInstanceService } from '../../services/free-switch-instance.service';
import { FreeSwitchInstanceDto } from '../../models/free-switch-instance.dto';
import { ExecuteFreeSwitchConsoleCommandInput } from '../../models/execute-free-switch-console-command-input.dto';
import { FreeSwitchConsoleCommandResultDto } from '../../models/free-switch-console-command-result.dto';
import { ConsoleEntry } from '../../models/console-entry.model';
import { ConsolePreset } from '../../models/console-preset.model';

/** Maximum command length; mirrors ExecuteFreeSwitchConsoleCommandInput.MaxCommandLength on the backend. */
const MAX_COMMAND_LENGTH = 512;

/** How many commands the up/down history keeps, oldest dropped first. */
const MAX_HISTORY_LENGTH = 100;

const CONSOLE_PRESETS: ConsolePreset[] = [
  { command: 'status' },
  { command: 'show channels as json' },
  { command: 'sofia status' },
  { command: 'reloadxml' },
];

@Component({
  selector: 'app-freeswitch-instance-console',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, LocalizationPipe, DialogModule, TooltipModule],
  templateUrl: './freeswitch-instance-console.component.html',
  styleUrls: ['./freeswitch-instance-console.component.scss'],
})
export class FreeSwitchInstanceConsoleComponent
  extends BaseFormComponent
  implements OnChanges, OnDestroy
{
  private readonly fb = inject(FormBuilder);
  private readonly localization = inject(LocalizationService);
  private readonly freeSwitchInstanceService = inject(FreeSwitchInstanceService);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() instance: FreeSwitchInstanceDto | null = null;

  @ViewChild('screen') private screenRef?: ElementRef<HTMLElement>;
  @ViewChild('commandInput') private commandInputRef?: ElementRef<HTMLInputElement>;

  dialogVisible = false;

  /** Append-only scrollback: every command typed and every response received, in order. */
  entries: ConsoleEntry[] = [];

  readonly presets = CONSOLE_PRESETS;

  /** Commands as typed, oldest first — walked with the up/down arrows. */
  private history: string[] = [];

  /**
   * Cursor into {@link history} while browsing with the arrow keys, counted back from the newest
   * entry (0 = newest). `-1` means "not browsing", i.e. the input holds the user's own draft.
   */
  private historyCursor = -1;

  /** The half-typed line stashed when arrow-up starts browsing, restored on arrow-down past the end. */
  private draft = '';

  private nextEntryId = 1;
  private scrollTimer?: ReturnType<typeof setTimeout>;
  private focusTimer?: ReturnType<typeof setTimeout>;

  constructor(changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  get isBusy(): boolean {
    return this.isSubmitting;
  }

  get hasEntries(): boolean {
    return this.entries.length > 0;
  }

  /**
   * The prompt shown before each command. Deliberately the XML-RPC host the command is actually sent
   * to — `webRtcHost` when set and `ipAddress` otherwise, matching
   * `FreeSwitchInstanceEntity.XmlRpcHost()` on the backend — so a misconfigured host is visible in
   * the console itself instead of only in a failed request.
   */
  get prompt(): string {
    const host = this.instance?.webRtcHost?.trim() || this.instance?.ipAddress?.trim();
    return `${host || this.instance?.name || 'freeswitch'}>`;
  }

  get canExecute(): boolean {
    return !this.isBusy && !!this.commandValue;
  }

  private get commandValue(): string {
    return (this.form?.get('command')?.value ?? '').trim();
  }

  trackByPreset(_index: number, preset: ConsolePreset): string {
    return preset.command;
  }

  trackByEntry(_index: number, entry: ConsoleEntry): number {
    return entry.id;
  }

  formatDuration(durationMs: number): string {
    return this.localization.instant('FreeswitchIntegration::ConsoleDurationMs', String(durationMs));
  }

  protected buildForm(): FormGroup {
    return this.fb.group({
      command: this.fb.control('', [Validators.required, Validators.maxLength(MAX_COMMAND_LENGTH)]),
    });
  }

  protected override afterFormInit(): void {
    this.dialogVisible = !!this.visible;
    this.focusInputSoon();
  }

  ngOnChanges(): void {
    if (!this.form) {
      return;
    }

    this.dialogVisible = !!this.visible;

    if (this.visible) {
      // A fresh open starts a fresh session: empty screen, empty history.
      this.entries = [];
      this.history = [];
      this.historyCursor = -1;
      this.draft = '';
      this.resetForm({ command: '' });
      this.focusInputSoon();
    }
  }

  ngOnDestroy(): void {
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }
    if (this.focusTimer) {
      clearTimeout(this.focusTimer);
    }
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  close(): void {
    this.visibleChange.emit(false);
  }

  execute(): void {
    this.onSubmit();
  }

  /**
   * Wipes the screen only — like `cls`, the command history stays walkable with the arrow keys and
   * whatever is half-typed in the input is left alone.
   */
  clear(): void {
    this.entries = [];
    this.cdr.markForCheck();
    this.focusInputSoon();
  }

  applyPreset(preset: ConsolePreset): void {
    this.setCommand(preset.command);
    this.focusInputSoon();
  }

  /** Clicking anywhere on the black area puts the caret back on the prompt, as a terminal would. */
  focusInput(): void {
    this.commandInputRef?.nativeElement.focus();
  }

  onCommandKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.walkHistory(1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.walkHistory(-1);
        break;
      case 'Escape':
        // cmd clears the current input line on Escape; stopped here so the dialog stays open.
        event.preventDefault();
        event.stopPropagation();
        this.setCommand('');
        this.historyCursor = -1;
        break;
      default:
        break;
    }
  }

  protected submitRequest(): Observable<FreeSwitchConsoleCommandResultDto> {
    const command = this.commandValue;
    const input: ExecuteFreeSwitchConsoleCommandInput = { command };

    this.pushEntry({ kind: 'command', prompt: this.prompt, text: command });
    this.rememberInHistory(command);

    // Echoing the command into the transcript is what preserves the record, so the input line is
    // freed immediately — the same moment cmd hands you a new empty line after Enter.
    this.setCommand('');
    this.submitted = false;

    return this.freeSwitchInstanceService.executeConsoleCommand(this.instance!.id, input).pipe(
      tap(result =>
        this.pushEntry({
          kind: 'output',
          text:
            result.output ||
            this.localization.instant('FreeswitchIntegration::ConsoleEmptyOutput'),
          durationMs: result.durationMs,
        }),
      ),
    );
  }

  protected override onSubmitSuccess(): void {
    // No toast: a terminal reports into its own transcript.
    this.cdr.markForCheck();
    this.scrollToBottomSoon();
    this.focusInputSoon();
  }

  protected override onSubmitError(error: unknown): void {
    this.pushEntry({ kind: 'error', text: this.extractErrorText(error) });
    this.focusInputSoon();
  }

  protected override onSubmitInvalid(): void {
    // Enter on an empty line does nothing, exactly like a shell — no validation message.
    this.submitted = false;
  }

  private pushEntry(entry: Omit<ConsoleEntry, 'id'>): void {
    this.entries = [...this.entries, { ...entry, id: this.nextEntryId++ }];
    this.cdr.markForCheck();
    this.scrollToBottomSoon();
  }

  private rememberInHistory(command: string): void {
    this.historyCursor = -1;
    this.draft = '';

    // Repeating the same command back-to-back should not add a second slot to walk past.
    if (this.history[this.history.length - 1] === command) {
      return;
    }

    this.history = [...this.history, command].slice(-MAX_HISTORY_LENGTH);
  }

  /** `direction` +1 walks towards older commands (arrow up), -1 back towards the draft. */
  private walkHistory(direction: 1 | -1): void {
    if (!this.history.length) {
      return;
    }

    if (this.historyCursor === -1) {
      if (direction === -1) {
        return;
      }
      this.draft = this.form.get('command')?.value ?? '';
    }

    const nextCursor = this.historyCursor + direction;

    if (nextCursor < 0) {
      this.historyCursor = -1;
      this.setCommand(this.draft);
      return;
    }

    if (nextCursor > this.history.length - 1) {
      return;
    }

    this.historyCursor = nextCursor;
    this.setCommand(this.history[this.history.length - 1 - nextCursor]);
  }

  private setCommand(value: string): void {
    this.form.get('command')?.setValue(value);
    this.cdr.markForCheck();
  }

  /**
   * Pulls the readable text out of an ABP error envelope (`{ error: { message, details } }`),
   * falling back to the transport message.
   */
  private extractErrorText(error: unknown): string {
    const response = error as {
      error?: { error?: { message?: string; details?: string } };
      message?: string;
    };

    const abpError = response?.error?.error;
    if (abpError?.message) {
      return abpError.details ? `${abpError.message}\n${abpError.details}` : abpError.message;
    }

    return response?.message || this.localizationService.instant('::SaveFailed');
  }

  /**
   * Scrolls the transcript to the newest line once the view has rendered it — a timeout rather than
   * a direct assignment, because the pushed entry is not in the DOM yet when pushEntry returns.
   */
  private scrollToBottomSoon(): void {
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }

    this.scrollTimer = setTimeout(() => {
      const screen = this.screenRef?.nativeElement;
      if (screen) {
        screen.scrollTop = screen.scrollHeight;
      }
    });
  }

  private focusInputSoon(): void {
    if (this.focusTimer) {
      clearTimeout(this.focusTimer);
    }

    this.focusTimer = setTimeout(() => this.commandInputRef?.nativeElement.focus());
  }
}
